import { NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { GiftcardsAmountSelectorComponentDef } from '@df/module-giftcards/giftcards-amount-selector.component.def';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs';
import { IfStoreDirective } from '../../multisite/if-store.directive';
import { NMultisiteModal } from '../../multisite/modal/multisite-modal.interfaces';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { GiftcardsProductViewComponent } from './giftcards-product-view.component';
import { ValidateNumberDirective } from '@df/ui/form/validate-number.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
@Component({
    selector: 'giftcards-amount-selector',
    templateUrl: './giftcards-amount-selector.html',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./giftcards-amount-selector.scss'],
    standalone: true,
    imports: [
        FormsModule,
        GridModule,
        ICmsClientModule,
        IfStoreDirective,
        InputWrapComponent,
        InputWrapBodyDirective,
        NgClass,
        PriceComponent,
        TranslationModule,
        SizeClassDirective,
        ValidateNumberDirective
    ]
})
export class GiftcardsAmountSelectorComponent extends GiftcardsAmountSelectorComponentDef implements AfterViewInit, OnDestroy {
    maxError = false;
    minError = false;

    /**
     * Input element
     */
    @ViewChild('inputAmount')
    input?: ElementRef;

    @Input()
    form?: NgForm;

    show = false;
    private subscriptions = new Subscription();

    protected readonly multisiteModalId = NMultisiteModal.componentId;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        private giftcardsProductViewComponent: GiftcardsProductViewComponent
    ) {
        super(changeDetectorRef);
    }

    ngOnInit(): void {
        //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
        //Add 'implements OnInit' to the class.
        this.subscriptions.add(this.app.client.obs.breakpoint.subscribe(() => this.detectChanges()));
    }
    ngAfterViewInit(): void {
        this.subscriptions.add(
            this.form?.ngSubmit.subscribe(() => {
                this.updateCustomErrors();
                this.detectChanges();
            })
        );
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    toggleCustomAmountField(value: boolean = true) {
        this.show = !!this.request?.am_giftcard_amount_custom || value;
        if (value) {
            delete this.request?.am_giftcard_amount;
            this.input?.nativeElement.focus();
        }
        if (!value) {
            this.clearCustomValueErrors();
        }
        this.detectChanges();
    }
    onKeypress(event: KeyboardEvent) {
        // Allow key codes for special events. Reflect :
        // Backspace, tab, end, home
        const specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', '-', 'ArrowLeft', 'ArrowRight', 'Del', 'Delete'];
        // Allow decimal numbers and negative values
        const regex: RegExp = new RegExp('^\\d{1,}[.]?\\d{0,2}$', 'g');
        const el: HTMLInputElement = event.target as HTMLInputElement;
        // Allow Backspace, tab, end, and home keys
        if (specialKeys.indexOf(event.key) !== -1) {
            return;
        }
        const current: string = el.value;
        const startPosition = el.selectionStart || 0;
        const endPosition = el.selectionEnd || 0;
        const next: string = [current.slice(0, startPosition), event.key, current.slice(endPosition)].join('');
        // prevent enter char
        if (next && !String(next).match(regex)) {
            event.preventDefault();
        }
        this.detectChanges();
    }
    override selectAmount(value: number): void {
        if (this.request) {
            if (this.isCorrectAmount(value)) {
                this.request.am_giftcard_amount = value;
            } else {
                this.request.am_giftcard_amount = undefined;
            }
            if (this.request.am_giftcard_amount_custom) {
                delete this.request.am_giftcard_amount_custom;
            }
            this.toggleCustomAmountField(false);
            this.triggerUpdate();
        }
    }
    override onCustomAmountChange(): void {
        const customAmount = this.request?.am_giftcard_amount_custom;
        if (typeof customAmount === 'number' && this.isCorrectCustomAmount(customAmount) && this.request?.am_giftcard_amount) {
            delete this.request?.am_giftcard_amount;
        }
        this.triggerUpdate();
        this.detectChanges();
    }
    get amount(): number | undefined {
        if (this.request) {
            return this.request.am_giftcard_amount || this.request.am_giftcard_amount_custom;
        }
        return undefined;
    }
    get displayCustomFieldInputError(): boolean {
        return !this.minAmount && !this.maxAmount && !!this.giftcardsProductViewComponent.form?.['submitAttempt'];
    }
    protected updateCustomErrors(): void {
        const value = this.request?.am_giftcard_amount_custom || 0;
        this.minError = this.isMinValueError(value);
        this.maxError = this.isMaxValueError(value);
    }
    private clearCustomValueErrors(): void {
        this.maxError = false;
        this.minError = false;
    }

    get currencySymbol(): string | undefined {
        const base = this.app.data.currency.base;

        const currency = this.app.data.currency.list.find(curr => curr.code === base);
        if (currency) {
            return currency.symbol;
        }
        return undefined;
    }

    protected withCurrencySymbol(amount?: number): string {
        if (!amount) {
            return '';
        }
        return this.currencySymbol ? this.currencySymbol + amount : amount.toString();
    }
}
